import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { RegStep } from './components/registration-stepper/registration-stepper.component';
import { getAllowedSignupRoute, isSignupCompleted } from './signup-user';

@Component({
  selector: 'app-registration',
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.scss']
})
export class RegistrationComponent implements OnInit, OnDestroy {
  loader = false;
  activeTab: 'business' | 'vehicles' | 'drivers' = 'business';
  currentStep = 'registration-details';
  steps: RegStep[] = [];
  businessComplete = false;

  private routerSub: Subscription;

  readonly businessSteps: RegStep[] = [
    { id: 'registration-details', label: 'Registration Details', link: '/registration/registration-details' },
    { id: 'verification', label: 'Verification', link: '/registration/verification' },
    { id: 'payout-setup', label: 'Payout setup', link: '/registration/payout-setup' },
    { id: 'tcp-insurance', label: 'TCP & Insurance', link: '/registration/insurance-details' }
  ];

  readonly vehicleSteps: RegStep[] = [
    { id: 'add-vehicle', label: 'Add Vehicle', link: '/registration/vehicles' }
  ];

  readonly driverSteps: RegStep[] = [
    { id: 'profile-details', label: 'Profile Details', link: '/registration/drivers/profile-details' },
    { id: 'assign-vehicle', label: 'Assign Vehicle', link: '/registration/drivers/assign-vehicle' },
    { id: 'verification', label: 'Verification', link: '/registration/drivers/verification' },
    { id: 'payout-setup', label: 'Payout invite', link: '/registration/drivers/payout-setup' },
    { id: 'preview', label: 'Preview', link: '/registration/drivers/preview' }
  ];

  constructor(private router: Router) {}

  ngOnInit() {
    this.refreshBusinessComplete();
    this.syncFromUrl(this.router.url);
    this.routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.refreshBusinessComplete();
        this.syncFromUrl(event.urlAfterRedirects || event.url);
      });
  }

  ngOnDestroy() {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }

  changeLoader() {
    this.loader = !this.loader;
  }

  goHome() {
    this.router.navigateByUrl(getAllowedSignupRoute());
  }

  goTab(tab: 'business' | 'vehicles' | 'drivers') {
    if (tab !== 'business' && !this.businessComplete) {
      return;
    }
    if (tab === 'business') {
      // Resume current incomplete business step — never jump back to step 1
      this.router.navigateByUrl(getAllowedSignupRoute());
    } else if (tab === 'vehicles') {
      this.router.navigate(['/registration/vehicles']);
    } else {
      this.router.navigate(['/registration/drivers/profile-details']);
    }
  }

  private refreshBusinessComplete() {
    try {
      this.businessComplete = isSignupCompleted() || localStorage.getItem('reg-business-complete') === '1';
      if (this.businessComplete) {
        localStorage.setItem('reg-business-complete', '1');
      }
    } catch (e) {
      this.businessComplete = localStorage.getItem('reg-business-complete') === '1';
    }
  }

  private syncFromUrl(url: string) {
    const path = (url || '').split('?')[0];
    if (path.indexOf('/registration/drivers') !== -1) {
      this.activeTab = 'drivers';
      this.steps = this.driverSteps;
      if (path.indexOf('assign-vehicle') !== -1) {
        this.currentStep = 'assign-vehicle';
      } else if (path.indexOf('drivers/verification') !== -1) {
        this.currentStep = 'verification';
      } else if (
        path.indexOf('drivers/payout-setup') !== -1
        || path.indexOf('drivers/payout-return') !== -1
        || path.indexOf('bank-information') !== -1
      ) {
        this.currentStep = 'payout-setup';
      } else if (path.indexOf('preview') !== -1) {
        this.currentStep = 'preview';
      } else {
        this.currentStep = 'profile-details';
      }
      return;
    }

    if (path.indexOf('/registration/vehicles') !== -1) {
      this.activeTab = 'vehicles';
      this.steps = this.vehicleSteps;
      this.currentStep = 'add-vehicle';
      return;
    }

    this.activeTab = 'business';
    this.steps = this.businessSteps;
    if (path.indexOf('payout-setup') !== -1 || path.indexOf('payout-return') !== -1 || path.indexOf('bank-info') !== -1) {
      this.currentStep = 'payout-setup';
    } else if (path.indexOf('insurance-details') !== -1) {
      this.currentStep = 'tcp-insurance';
    } else if (path.indexOf('verification') !== -1) {
      this.currentStep = 'verification';
    } else {
      this.currentStep = 'registration-details';
    }
  }
}
